import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Fingerprint, KeyRound, Loader2, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useChangePassword, useMe } from '@/features/auth/hooks'
import { useDeletePasskey, usePasskeys, useRegisterPasskey } from '@/features/auth/hooks'
import type { Passkey } from '@/types/auth'

export const Route = createFileRoute('/_authenticated/settings/security')({
  component: SecuritySettingsPage,
})

function formatDate(iso?: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString()
}

function PasswordCard() {
  const { data: me } = useMe()
  const change = useChangePassword()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const hasPassword = !!me?.has_password

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSaved(false)
    if (hasPassword && !current) {
      setError('Enter your current password.')
      return
    }
    if (next.length < 8) {
      setError('New password must be at least 8 characters.')
      return
    }
    if (next !== confirm) {
      setError('New passwords do not match.')
      return
    }
    change.mutate(
      { current, next },
      {
        onSuccess: () => {
          setCurrent('')
          setNext('')
          setConfirm('')
          setSaved(true)
        },
        onError: (err) => setError(err.message || 'Failed to update password'),
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" /> {hasPassword ? 'Change password' : 'Set a password'}
        </CardTitle>
        <CardDescription>
          {hasPassword
            ? 'Changing your password signs out every other device.'
            : 'You currently sign in with a passkey only. Setting a password also enables password sign-in.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="max-w-sm space-y-3">
          {hasPassword && (
            <div className="grid gap-2">
              <Label htmlFor="cur-password">Current password</Label>
              <Input
                id="cur-password"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                disabled={change.isPending}
              />
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              placeholder="min 8 characters"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              disabled={change.isPending}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={change.isPending}
            />
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          {saved && <p className="text-sm text-green-600">Password updated.</p>}
          <Button type="submit" disabled={change.isPending}>
            {change.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {hasPassword ? 'Update password' : 'Set password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function SecuritySettingsPage() {
  const { data: me } = useMe()
  const { data: passkeys, isLoading, isError } = usePasskeys()
  const register = useRegisterPasskey()
  const remove = useDeletePasskey()
  const [nameOpen, setNameOpen] = useState(false)
  const [removingPasskey, setRemovingPasskey] = useState<Passkey | null>(null)
  const [name, setName] = useState('')
  const [registerError, setRegisterError] = useState<string | null>(null)

  const onRegister = () => {
    setRegisterError(null)
    register.mutate(name.trim() || 'Passkey', {
      onSuccess: () => setNameOpen(false),
      onError: (err) =>
        setRegisterError(
          err.message?.includes('passkey')
            ? err.message
            : 'Passkey registration failed or was canceled',
        ),
    })
  }

  return (
    <div className="container mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Passkeys & security</h1>
          <p className="text-muted-foreground text-sm">
            Signed in as @{me?.username}
            {me?.kind === 'fp_admin' ? ' (FlowPilot support)' : ''}
          </p>
        </div>
      </div>

      <PasswordCard />

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Fingerprint className="h-4 w-4" /> Your passkeys
            </CardTitle>
            <CardDescription>
              Sign in without a password using Touch ID, Windows Hello, or a hardware key.
            </CardDescription>
          </div>
          <Button onClick={() => setNameOpen(true)} disabled={register.isPending}>
            {register.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Add passkey
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <p className="text-destructive text-sm">Failed to load passkeys.</p>
          ) : (passkeys ?? []).length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No passkeys yet. Add one to enable passwordless sign-in.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(passkeys ?? []).map((passkey) => (
                  <TableRow key={passkey.id}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        <Fingerprint className="text-muted-foreground h-4 w-4" />
                        {passkey.name || 'Passkey'}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(passkey.created_at)}</TableCell>
                    <TableCell className="text-sm">{formatDate(passkey.last_used_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={remove.isPending}
                        onClick={() => setRemovingPasskey(passkey)}
                        title="Remove passkey"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {registerError && <p className="text-destructive mt-3 text-sm">{registerError}</p>}
        </CardContent>
      </Card>

      <Dialog open={nameOpen} onOpenChange={(open) => !open && setNameOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name this passkey</DialogTitle>
            <DialogDescription>
              A label like “MacBook Touch ID” or “YubiKey” helps you recognize it later.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="pk-name">Name</Label>
            <Input
              id="pk-name"
              value={name}
              placeholder="e.g. iPhone"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNameOpen(false)}
              disabled={register.isPending}
            >
              Cancel
            </Button>
            <Button onClick={onRegister} disabled={register.isPending}>
              {register.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Passkey removal confirmation — removing the last passkey can lock
          the user out of passwordless sign-in. */}
      <Dialog open={!!removingPasskey} onOpenChange={(o) => !o && setRemovingPasskey(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove passkey “{removingPasskey?.name || 'Passkey'}”?</DialogTitle>
            <DialogDescription>
              You will not be able to sign in with this passkey again. Password and other passkeys
              keep working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemovingPasskey(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() =>
                removingPasskey &&
                remove.mutate(removingPasskey.id, { onSuccess: () => setRemovingPasskey(null) })
              }
            >
              {remove.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove Passkey
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
